/**
 * Copyright 2016 Taito United Oy
 * All rights reserved.
 */

import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

// Messages
import messages from './messages/ReportRating_messages';

// Components
import IconRating from '../common/IconRating';
import LoadingIndicator from '../common/LoadingIndicator';
import LoginWidget from '../user/LoginWidget';
import Button from '../../components/Button';

const propTypes = {
  report: PropTypes.object.isRequired,
  review: PropTypes.object,
  session: PropTypes.object.isRequired,
  dashMode: PropTypes.bool,
  onLogin: PropTypes.func.isRequired,
  saveReview: PropTypes.func,
};

const defaultProps = {
  review: null,
  dashMode: false,
  saveReview: () => {},
};

class ReportRating extends PureComponent {
  constructor(props) {
    super(props);

    this.reviewTypes = ['overall', 'price', 'quality', 'thumbs'];

    this.state = {
      editingReview: false,
      thankForReviewing: false,
      rating: {
        overall: 0,
        price: 0,
        quality: 0,
        thumbs: 0,
      },
    };
  }

  getRatingValue = type => {
    const { editingReview } = this.state;
    if (editingReview) {
      return this.state.rating[type];
    }
    return this.props.report.review_average[type].value;
  }

  addReview = () => {
    const { review } = this.props;
    const { rating } = this.state;
    this.setState({ editingReview: true, rating: { ...(review || rating) }});
  };

  changeRating = rating => {
    this.setState({
      rating: {
        ...this.state.rating,
        [rating.themeName]: rating.rating,
      },
    });
  };

  saveReview = () => {
    const { session } = this.props;
    const preferredOrder = ['facebook', 'google', 'zeckit'];

    const provider = preferredOrder.find(
      _provider => !!session.logins[_provider],
    );

    const login = session.logins[provider];

    this.setState({
      thankForReviewing: true,
      editingReview: !this.state.editingReview,
    });

    this.props.report.showSpinner = true;

    this.props.saveReview({
      provider,
      country: this.props.report.country,
      identifier: this.props.report.identifier,
      review: this.state.rating,
      token: login.token,
    });
  };

  get companyHasReviews() {
    return !!this.props.report.review_average.overall.count;
  }

  get userIsLoggedIn() {
    const { session: { logins }} = this.props;
    // tilaajavastuu login is not authorized for reviewing
    return (
      Object.keys(logins).length > 1
      || (Object.keys(logins).length > 0 && !logins.tilaajavastuu)
    );
  }

  get userHasReviewed() {
    return !!this.props.review.overall;
  }

  render() {
    const {
      dashMode,
      onLogin,
      report: {
        country,
        identifier,
        showSpinner,
      },
    } = this.props;
    const { editingReview } = this.state;

    return (
      <div>
        {/* Show login if user wants to edit but is not logged in */}
        {(editingReview && !this.userIsLoggedIn) && (
          <React.Fragment>
            <LoginWidget
              country={country}
              identifier={identifier}
              onLogin={onLogin}
            />
            <LoginDisclaimer>
              <FormattedMessage
                {...messages.loginDisclaimer}
                info="Explains why login is required"
              />
            </LoginDisclaimer>
          </React.Fragment>
        )}

        {/* If there are no reviews prompt the user to add one */}
        {(!this.companyHasReviews && !editingReview) && (
          <Button onClick={this.addReview} type="success" wrap>
            <FormattedMessage
              {...messages.notYetRated}
              info="Company has not been rated yet"
            />
          </Button>
        )}

        {/* Show the ratings if there are any, or the user is editing */}
        {((!editingReview && this.companyHasReviews)
          || (editingReview && this.userIsLoggedIn)) &&
          this.reviewTypes.map(type => (
            <RatingRow key={type}>
              <RatingLabel>
                <FormattedMessage {...messages[`rating_${type}`]} />
              </RatingLabel>

              <IconRating
                onChange={this.changeRating}
                readonly={!this.userIsLoggedIn || !editingReview}
                reviewValue={this.getRatingValue(type)}
                themeName={type}
              />
            </RatingRow>
          ))
        }

        {/* Show controls only when not in company dash */}
        {!dashMode && (
          <RatingControls>
            {this.state.thankForReviewing && (
              <React.Fragment>
                {showSpinner && (
                  <LoadingIndicator color="#fff" type="beat" />
                )}
                {!showSpinner && (
                  <FormattedMessage
                    {...messages.thankForReviewing}
                    info="thankForReviewing"
                  />
                )}
              </React.Fragment>
            )}
            {!this.state.thankForReviewing && (
              <React.Fragment>
                {(this.companyHasReviews && !editingReview) && (
                  <Button onClick={this.addReview} type="success">
                    {!this.userHasReviewed && (
                      <FormattedMessage
                        {...messages.newReview}
                        info="Submit new review"
                      />
                    )}
                    {this.userHasReviewed && (
                      <FormattedMessage
                        {...messages.editReview}
                        info="Edit existing review"
                      />
                    )}
                  </Button>
                )}
                {(this.userIsLoggedIn && editingReview) && (
                  <Button onClick={this.saveReview} type="success">
                    <FormattedMessage
                      {...messages.saveReview}
                      info="Save review"
                    />
                  </Button>
                )}
              </React.Fragment>
            )}
          </RatingControls>
        )}

        {/* If there are reviews, show link to view them all */}
        {this.companyHasReviews && (
          <Link to={`/selvitys/${country}/${identifier}/reviews`}>
            <FormattedMessage
              {...messages.showAllReviews}
              info="Show all reviews"
            />
          </Link>
        )}
      </div>
    );
  }
}

ReportRating.propTypes = propTypes;
ReportRating.defaultProps = defaultProps;

const LoginDisclaimer = styled.p`
  font-size: ${props => props.theme['font-size-small']};
  line-height: ${props => props.theme['line-height-small']};
`;

const RatingControls = styled.div`
  margin: 12px 0;
`;

const RatingLabel = styled.span`
  font-weight: bold;
`;

const RatingRow = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  margin-bottom: 6px;
  max-width: 480px;
  width: 100%;

  @media (min-width: 480px) {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
  }
`;

export default ReportRating;
